import React, { useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';

// ─── MismatchFlagModal ────────────────────────────────────────────────────────
// Props:
//   module        — 'lead' | 't2m' | 'pb101'
//   recordId      — action_id / trial_id / ltp_id
//   memberId      — member_id
//   locationId    — location_id
//   memberName    — display name
//   originalStatus   — current status on the record
//   originalPipeline — pipeline string (lead only)
//   onClose       — dismiss without flagging
//   onFlagged     — called after successful flag

export default function MismatchFlagModal({
  module, recordId, memberId, locationId,
  memberName, originalStatus, originalPipeline,
  onClose, onFlagged,
}) {
  const { post } = useApi();
  const { user }  = useAuth();
  const [note, setNote]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  const moduleLabel = module === 'lead' ? 'Lead Engine' : module === 't2m' ? 'T2M' : 'PB101';

  async function handleSubmit() {
    if (!note.trim()) { setError('Please describe the data issue before flagging.'); return; }
    setSaving(true);
    setError(null);
    try {
      await post('/mismatch/flag', {
        module,
        recordId:        String(recordId),
        memberId,
        locationId,
        originalStatus,
        originalPipeline: originalPipeline || null,
        flagNote:         note.trim(),
      });
      onFlagged && onFlagged();
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to flag record. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="modal-overlay" onClick={onClose} style={{ padding: 0 }} />
      <div
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(500px, 95vw)',
          background: 'var(--surface-1, #111)',
          border: '1px solid var(--border, #222)',
          borderRadius: 10,
          zIndex: 1001,
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          background: 'rgba(239,68,68,0.06)',
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444' }}>
              🚩 Flag Data Mismatch
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
              {memberName} · {moduleLabel} · {locationId}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid var(--border)',
              color: 'var(--text-secondary)', borderRadius: 6,
              width: 28, height: 28, cursor: 'pointer', fontSize: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 18px' }}>

          {/* Info box */}
          <div style={{
            background: 'rgba(234,179,8,0.08)',
            border: '1px solid rgba(234,179,8,0.2)',
            borderRadius: 6, padding: '10px 12px',
            fontSize: 12, color: 'var(--text-secondary)',
            marginBottom: 14, lineHeight: 1.55,
          }}>
            This record will be moved to the <strong style={{ color: 'var(--yellow)' }}>Mismatch Queue</strong> and frozen — 
            the pipeline will not touch it until it's resolved. Bryon will review your note and either fix the data or return it to the queue.
          </div>

          {/* Note field */}
          <div style={{ marginBottom: 14 }}>
            <label style={{
              display: 'block', fontSize: 11, fontWeight: 700,
              color: 'var(--text-secondary)', textTransform: 'uppercase',
              letterSpacing: '0.06em', marginBottom: 6,
            }}>
              What looks wrong? *
            </label>
            <textarea
              className="field-textarea"
              value={note}
              onChange={e => { setNote(e.target.value); setError(null); }}
              placeholder="Describe the data issue — e.g. 'This person shows as active in LE but I can see them as a current member in the club'"
              style={{ minHeight: 100, fontSize: 13 }}
              autoFocus
            />
          </div>

          {error && (
            <div style={{
              padding: '8px 12px', borderRadius: 6, marginBottom: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              fontSize: 12, color: '#ef4444',
            }}>
              {error}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !note.trim()}
              style={{
                background: saving ? '#555' : '#ef4444',
                color: '#fff', border: 'none',
                borderRadius: 6, padding: '7px 18px',
                fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Flagging…' : '🚩 Flag & Move to Mismatch'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
